import { useEffect, useMemo, useState } from "react";
import {
  offerIdsWithSavedAngles,
  productIdsWithSavedAngles,
  type AdsExpressNarrativeAngleAttachment,
  type adsExpressAttachmentFormState,
} from "@shared/ad-generation-request";

type Attachment = ReturnType<typeof adsExpressAttachmentFormState>;

// Hydration is an explicit event, not an inference from a missing saved angle.
export function useAdsExpressItemFilter({
  brandId, activeGenerationId, attachment, angles, loadingAngles, angleMode,
  savedAngleChoices, setProductId, setOfferId, setAngleId,
}: {
  brandId?: string;
  activeGenerationId: string | null;
  attachment: Attachment;
  angles: readonly AdsExpressNarrativeAngleAttachment[];
  loadingAngles: boolean;
  angleMode: "saved" | "custom";
  savedAngleChoices: readonly { id: string }[];
  setProductId: (id: string) => void;
  setOfferId: (id: string) => void;
  setAngleId: (id: string) => void;
}) {
  const [history, setHistory] = useState<{ brandId?: string; runId: string; attachment: Attachment | null } | null>(null);
  const productsWithAngles = useMemo(
    () => productIdsWithSavedAngles(angles, attachment.personaId),
    [angles, attachment.personaId],
  );
  const offersWithAngles = useMemo(
    () => offerIdsWithSavedAngles(angles, attachment.personaId),
    [angles, attachment.personaId],
  );
  const filterByAngles = angleMode === "saved" && !!attachment.personaId && !loadingAngles;
  const retained = history?.brandId === brandId &&
    history?.runId === activeGenerationId ? history.attachment : null;
  // Funnel-stage and angle-picker changes do not release historical items.
  const preservingHistoricalAttachment = !!retained &&
    (["personaId", "promotedItemType", "productId", "offerId"] as const)
      .every((key) => retained[key] === attachment[key]);

  const preserveHydratedAttachment = (runId: string, tuple: Attachment) => {
    setHistory({ brandId, runId, attachment: { ...tuple } });
  };
  const releaseHistoricalAttachment = () => {
    setHistory((current) => current ? { ...current, attachment: null } : null);
  };

  useEffect(() => {
    // Wait for the new run's entire tuple, even when its response is delayed.
    if (activeGenerationId && (
      history?.brandId !== brandId || history?.runId !== activeGenerationId
    )) return;
    if (preservingHistoricalAttachment) return;
    if (!filterByAngles) return;
    const isOffer = attachment.promotedItemType === "offer";
    const itemId = isOffer ? attachment.offerId : attachment.productId;
    if (itemId && !(isOffer ? offersWithAngles : productsWithAngles).has(itemId)) {
      (isOffer ? setOfferId : setProductId)("");
      setAngleId("");
    } else if (attachment.angleId && !savedAngleChoices.some((a) => a.id === attachment.angleId)) {
      setAngleId("");
    }
  }, [brandId, activeGenerationId, attachment.personaId, attachment.promotedItemType,
    attachment.productId, attachment.offerId, attachment.angleId, filterByAngles, history, preservingHistoricalAttachment,
    productsWithAngles, offersWithAngles, savedAngleChoices, setProductId, setOfferId, setAngleId]);

  return { filterByAngles, productsWithAngles, offersWithAngles,
    preserveHydratedAttachment, releaseHistoricalAttachment, preservingHistoricalAttachment };
}
